/*
  This file is added to your codebase if you've excluded database integration from your stack when exporting from
  from Divjoy, but one of your components attempts to import this file and call one of its functions.
  Rather than break your project, we include this placeholder file so your codebase can still run.
  If you need database integration, re-export from Divjoy using the "SaaS" app type and select a database option.
  Otherwise, you can search your codebase for the function names you see below, remove all related code, then delete this file.
*/

import { getFirestore, collection, doc, getDoc, getDocs, query, where, addDoc, updateDoc, deleteDoc } from "firebase/firestore";
import { firebaseApp } from "./firebase";

const db = getFirestore(firebaseApp);

// Tables collection
export const tablesCollection = collection(db, "tables");

// Reservations collection
export const reservationsCollection = collection(db, "reservations");

// Counter seats collection
export const counterSeatsCollection = collection(db, "counterSeats");

// Get all tables
export async function getTables() {
  const querySnapshot = await getDocs(tablesCollection);
  return querySnapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  }));
}

// Get a specific table by ID
export async function getTable(tableId) {
  const docRef = doc(tablesCollection, tableId);
  const docSnap = await getDoc(docRef);
  if (docSnap.exists()) {
    return {
      id: docSnap.id,
      ...docSnap.data()
    };
  }
  return null;
}

// Utility function to format date as YYYY-MM-DD without timezone conversion
function formatDateAsYYYYMMDD(date) {
  // Get the year, month, and day components directly from the date
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  
  // Return YYYY-MM-DD format
  return `${year}-${month}-${day}`;
}

// Get all reservations for a specific date
export async function getReservationsForDate(dateStr) {
  const reservationsQuery = query(
    reservationsCollection,
    where("reservationDate", "==", dateStr)
  );
  
  const querySnapshot = await getDocs(reservationsQuery);
  return querySnapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  }));
}

// Get recent reservations (last month) for search functionality
export async function getRecentReservations() {
  // Calculate date 1 month ago
  const oneMonthAgo = new Date();
  oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
  const oneMonthAgoStr = formatDateAsYYYYMMDD(oneMonthAgo);
  
  const reservationsQuery = query(
    reservationsCollection,
    where("reservationDate", ">=", oneMonthAgoStr)
  );
  
  const querySnapshot = await getDocs(reservationsQuery);
  return querySnapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  }));
}

// Get all tables with sufficient seats
export async function getTablesWithSeats(partySize, isAdmin = false) {
  // Build the query - if admin, don't filter by isReservable
  let tableQuery;
  if (isAdmin) {
    tableQuery = query(
      tablesCollection,
      where("location", "==", "floor"),
      where("numSeats", ">=", partySize)
    );
  } else {
    tableQuery = query(
      tablesCollection,
      where("location", "==", "floor"),
      where("isReservable", "==", true),
      where("numSeats", ">=", partySize)
    );
  }
  
  const querySnapshot = await getDocs(tableQuery);
  return querySnapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  }));
}

// Get all tables that can be combined for a party
export async function getAllAvailableTables(isAdmin = false) {
  // Build the query - if admin, don't filter by isReservable
  let tableQuery;
  if (isAdmin) {
    tableQuery = query(
      tablesCollection,
      where("location", "==", "floor")
    );
  } else {
    tableQuery = query(
      tablesCollection,
      where("location", "==", "floor"),
      where("isReservable", "==", true)
    );
  }
  
  const querySnapshot = await getDocs(tableQuery);
  return querySnapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  }));
}

// Get available tables for a given date, time, and party size
export async function getAvailableTables(dateStr, startTime, endTime, partySize, excludeReservationId = null, seatType = 'table', isAdmin = false) {
  try {
    // Ensure partySize is a number
    partySize = Number(partySize);
    
    // Validate date format (YYYY-MM-DD)
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      console.error('Invalid date format. Expected YYYY-MM-DD');
      return [];
    }
    
    // Get all reservations for the date first (once)
    const reservations = await getReservationsForDate(dateStr);
    const filteredReservations = excludeReservationId
      ? reservations.filter(r => r.id !== excludeReservationId)
      : reservations;
    
    // Handle differently based on seat type
    if (seatType === 'counter') {
      // For counter seats, get all counter seats
      const counterSeats = await getCounterSeats(isAdmin);
      if (counterSeats.length === 0) return [];
      
      // Find counter seats without conflicts
      const availableCounterSeats = counterSeats.filter(seat => {
        try {
          const seatReservations = filteredReservations.filter(r => r.tableIds?.includes(seat.id));
          return !seatReservations.some(existing => {
            return startTime < existing.endTime && endTime > existing.startTime;
          });
        } catch (error) {
          console.error('Error filtering seat conflicts for seat', seat.id, error);
          return false;
        }
      });
      
      return availableCounterSeats;
    }
    
    // For tables, get all available tables of any size (only one query)
    const allTables = await getAllAvailableTables(isAdmin);
    
    // Filter out tables with conflicts
    const allAvailableTables = allTables.filter(table => {
      try {
        const tableReservations = filteredReservations.filter(r => r.tableIds?.includes(table.id));
        return !tableReservations.some(existing => {
          return startTime < existing.endTime && endTime > existing.startTime;
        });
      } catch (error) {
        console.error('Error filtering conflicts for table', table.id, error);
        return false;
      }
    });
    
    // APPROACH 1: First try to find single tables with enough capacity
    const singleTables = allAvailableTables.filter(table => table.numSeats >= partySize);
    
    if (singleTables.length > 0) {
      // Sort by closest fit (to avoid wasting large tables on small parties)
      singleTables.sort((a, b) => a.numSeats - b.numSeats);
      return [singleTables[0]];
    }
    
    // APPROACH 2: Find consecutive tables that together have enough capacity
    
    // Sort tables by their ID to find consecutive tables
    const tablesByID = [...allAvailableTables].sort((a, b) => {
      const aMatch = a.id.match(/(\d+)/);
      const bMatch = b.id.match(/(\d+)/);
      
      const aNum = aMatch ? parseInt(aMatch[0]) : 0;
      const bNum = bMatch ? parseInt(bMatch[0]) : 0;
      
      return aNum - bNum;
    });
    
    // Group tables into consecutive sequences
    const consecutiveGroups = [];
    let currentGroup = [];
    
    for (let i = 0; i < tablesByID.length; i++) {
      const currentTable = tablesByID[i];
      
      // Start a new group if this is the first table
      if (currentGroup.length === 0) {
        currentGroup.push(currentTable);
        continue;
      }
      
      // Get the ID numbers for comparison
      const currentMatch = currentTable.id.match(/(\d+)/);
      const previousMatch = currentGroup[currentGroup.length - 1].id.match(/(\d+)/);
      
      const currentNum = currentMatch ? parseInt(currentMatch[0]) : 0;
      const previousNum = previousMatch ? parseInt(previousMatch[0]) : 0;
      
      // Check if the tables are consecutive
      if (currentNum === previousNum + 1) {
        // Tables are consecutive, add to current group
        currentGroup.push(currentTable);
      } else {
        // Tables are not consecutive, start a new group
        if (currentGroup.length > 0) {
          consecutiveGroups.push([...currentGroup]);
        }
        currentGroup = [currentTable];
      }
    }
    
    // Add the last group if it's not empty
    if (currentGroup.length > 0) {
      consecutiveGroups.push([...currentGroup]);
    }
    
    // For each consecutive group, check if they have enough combined seats
    // Go through each group and sum up the seats to find a viable combination
    for (const group of consecutiveGroups) {
      let totalSeats = 0;
      const selectedTables = [];
      
      // Check if the entire group has enough capacity
      for (const table of group) {
        totalSeats += table.numSeats;
        selectedTables.push(table);
        
        // Stop adding tables once we have enough seats
        if (totalSeats >= partySize) {
          return selectedTables;
        }
      }
    }

    // Try to find consecutive subsets from larger groups for edge cases
    for (const group of consecutiveGroups) {
      // Only consider groups with at least 3 tables
      if (group.length >= 3) {
        // Try different starting points within this consecutive group
        for (let start = 0; start <= group.length - 3; start++) {
          let subsetSeats = 0;
          const subset = [];
          
          // Take up to 3+ consecutive tables from this subset
          for (let i = start; i < group.length; i++) {
            subsetSeats += group[i].numSeats;
            subset.push(group[i]);
            
            if (subsetSeats >= partySize) {
              return subset;
            }
          }
        }
      }
    }
    
    // No viable table combinations found
    return [];
  } catch (error) {
    console.error('Error checking reservation availability:', error);
    throw error;
  }
}

// Get ALL available tables/counters for admin seat selection without optimization
export async function getAllAvailableTablesForAdmin(dateStr, startTime, endTime, excludeReservationId = null, seatType = 'table') {
  try {
    // Validate date format (YYYY-MM-DD)
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      console.error('Invalid date format. Expected YYYY-MM-DD');
      return [];
    }
    
    // Get all reservations for the date first (once)
    const reservations = await getReservationsForDate(dateStr);
    const filteredReservations = excludeReservationId
      ? reservations.filter(r => r.id !== excludeReservationId)
      : reservations;
    
    // Get all tables of appropriate type (counter or floor)
    const allTablesData = await getTables();
    const tables = seatType === 'counter'
      ? allTablesData.filter(table => table.location === 'counter')
      : allTablesData.filter(table => table.location === 'floor');
    
    // Filter out tables with conflicts
    const availableTables = tables.filter(table => {
      try {
        const tableReservations = filteredReservations.filter(r => r.tableIds?.includes(table.id));
        return !tableReservations.some(existing => {
          return startTime < existing.endTime && endTime > existing.startTime;
        });
      } catch (error) {
        console.error('Error filtering conflicts for table', table.id, error);
        return false;
      }
    });
    
    // Sort tables by ID for better presentation
    return availableTables.sort((a, b) => {
      const aMatch = a.id.match(/(\d+)/);
      const bMatch = b.id.match(/(\d+)/);
      const aNum = aMatch ? parseInt(aMatch[0]) : 0;
      const bNum = bMatch ? parseInt(bMatch[0]) : 0;
      return aNum - bNum;
    });
  } catch (error) {
    console.error('Error getting all available tables for admin:', error);
    throw error;
  }
}

// Create a new reservation
export async function createReservation(reservationData) {
  // Use the date string directly since it's already in YYYY-MM-DD format
  const formattedReservationData = {
    ...reservationData,
    createdAt: new Date(),
    updatedAt: new Date()
  };
  
  return await addDoc(reservationsCollection, formattedReservationData);
}

// Get user's reservations
export async function getUserReservations(email) {
  const reservationsQuery = query(
    reservationsCollection,
    where("email", "==", email)
  );
  
  const querySnapshot = await getDocs(reservationsQuery);
  return querySnapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  }));
}

// Cancel a reservation
export async function cancelReservation(reservationId) {
  const docRef = doc(reservationsCollection, reservationId);
  await deleteDoc(docRef);
}

// Update a reservation
export async function updateReservation(reservationId, updateData, isAdmin = false) {
  console.log(`updateReservation called with isAdmin=${isAdmin}, updateData:`, updateData);
  
  const docRef = doc(reservationsCollection, reservationId);
  const reservationDoc = await getDoc(docRef);
  
  if (!reservationDoc.exists()) {
    throw new Error("Reservation not found");
  }

  const reservation = reservationDoc.data();
  console.log("Existing reservation:", reservation);

  // Check for conflicts if any of these fields are being updated
  if (updateData.partySize || updateData.startTime || updateData.endTime || updateData.reservationDate) {
    const startTime = updateData.startTime || reservation.startTime;
    const endTime = updateData.endTime || reservation.endTime;
    const reservationDate = updateData.reservationDate || reservation.reservationDate;
    const partySize = updateData.partySize || reservation.partySize;
    const seatType = updateData.seatType || reservation.seatType || 'table';

    console.log(`Checking for conflicts: time=${startTime}-${endTime}, date=${reservationDate}, partySize=${partySize}, seatType=${seatType}`);

    // Get available tables for the new time slot, excluding the current reservation
    const availableTables = await getAvailableTables(
      reservationDate, 
      startTime, 
      endTime, 
      partySize,
      reservationId, // Pass the current reservation ID to exclude it from conflict checks
      seatType,
      isAdmin // Pass isAdmin to consider non-reservable tables for admins
    );
    
    console.log("Available tables for update:", availableTables.map(t => t.id));
    
    // If no tables are available, there's a conflict
    if (availableTables.length === 0) {
      throw new Error("That time slot has been booked. Please refresh the page.");
    }

    // Only auto-set table IDs if they aren't manually specified in the update data
    // AND if the time, date, or party size has changed (to prevent double-booking)
    if (!updateData.tableIds && (updateData.startTime || updateData.reservationDate || updateData.partySize)) {
      // For counter seats, we can just use the available tables directly since getAvailableTables
      // already returns properly sorted and adjacent seats
      if (seatType === 'counter') {
        updateData.tableIds = availableTables.map(table => table.id);
      } else {
        // For regular tables, use all available tables (which might include combinations)
        updateData.tableIds = availableTables.map(table => table.id);
      }
      console.log("Auto-assigned tableIds:", updateData.tableIds);
    }
  }

  // Create an object with only the fields that are being updated
  const fieldsToUpdate = {
    updatedAt: new Date()
  };

  // Only include fields that are present in updateData
  if (updateData.name !== undefined) fieldsToUpdate.name = updateData.name;
  if (updateData.partySize !== undefined) fieldsToUpdate.partySize = updateData.partySize;
  if (updateData.startTime !== undefined) fieldsToUpdate.startTime = updateData.startTime;
  if (updateData.endTime !== undefined) fieldsToUpdate.endTime = updateData.endTime;
  if (updateData.reservationDate !== undefined) fieldsToUpdate.reservationDate = updateData.reservationDate;
  if (updateData.tableIds !== undefined) fieldsToUpdate.tableIds = updateData.tableIds;
  if (updateData.phoneNumber !== undefined) fieldsToUpdate.phoneNumber = updateData.phoneNumber;
  if (updateData.seatType !== undefined) fieldsToUpdate.seatType = updateData.seatType;
  if (updateData.specialRequests !== undefined) fieldsToUpdate.specialRequests = updateData.specialRequests;
  if (updateData.reminderEmail !== undefined) fieldsToUpdate.reminderEmail = updateData.reminderEmail;

  // If no fields to update, return early
  if (Object.keys(fieldsToUpdate).length === 1) { // Only has updatedAt
    return;
  }

  await updateDoc(docRef, fieldsToUpdate);
}

/**** USERS ****/

export function useUser(uid) {
  return {
    data: {
      uid: uid,
      email: "fake-user@gmail.com",
    },
    status: "success",
    error: null,
  };
}

export function getUser(uid) {
  Promise.resolve({
    uid: uid,
    email: "fake-user@gmail.com",
  });
}

export function createUser(uid, data) {
  return Promise.resolve(true);
}

export function updateUser(uid, data) {
  return Promise.resolve(true);
}

/**** ITEMS ****/
/* Example query functions (modify to your needs) */

export function useItem(id) {
  return {
    data: {
      id: id,
      name: "fake item",
    },
    status: "success",
    error: null,
  };
}

export function useItemsByOwner(owner) {
  return {
    data: [
      {
        id: 1,
        name: "fake item",
      },
    ],
    status: "success",
    error: null,
  };
}

export function createItem(data) {
  return Promise.resolve(true);
}

export function updateItem(id, data) {
  return Promise.resolve(true);
}

export function deleteItem(id) {
  return Promise.resolve(true);
}

// Get all counter seats
export async function getCounterSeats(isAdmin = false) {
  // Build the query - if admin, don't filter by isReservable
  let counterQuery;
  if (isAdmin) {
    counterQuery = query(
      tablesCollection,
      where("location", "==", "counter")
    );
  } else {
    counterQuery = query(
      tablesCollection,
      where("location", "==", "counter"),
      where("isReservable", "==", true)
    );
  }
  
  const querySnapshot = await getDocs(counterQuery);
  return querySnapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  }));
}
